import { useState } from "react";
import { useApp } from "@/app/container";
import { useLocale } from "@/app/i18n/LocaleProvider";
import { Button } from "../Button";
import styles from "../reminders/reminders.module.css";
import settingsStyles from "@/app/screens/SettingsScreen.module.css";

/** Settings section that hands out a transfer code or takes one in. `openEntry` starts with the code field shown. */
export function TransferSection({ openEntry = false }: { openEntry?: boolean }) {
  const app = useApp();
  const { t } = useLocale();
  const [issued, setIssued] = useState<{ code: string; expiresAt: string } | null>(null);
  const [enteringByHand, setEnteringByHand] = useState(false);
  const [dismissedOpen, setDismissedOpen] = useState(false);
  const entering = enteringByHand || (openEntry && !dismissedOpen);
  const setEntering = (value: boolean) => {
    setEnteringByHand(value);
    if (!value) setDismissedOpen(true);
  };
  const [code, setCode] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const issue = async () => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      setIssued(await app.transfer.createCode());
      app.analytics.track("transfer_code_issued");
    } catch {
      setError(t.transfer.failed);
    } finally {
      setBusy(false);
    }
  };

  const redeem = async () => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await app.transfer.redeem(code);
      app.analytics.track("transfer_code_redeemed");
      setNotice(t.transfer.done);
      setEntering(false);
      setCode("");
      setConfirming(false);
    } catch (cause) {
      setError((cause as { status?: number }).status === 404 ? t.transfer.invalid : t.transfer.failed);
      setConfirming(false);
    } finally {
      setBusy(false);
    }
  };

  const submit = async () => {
    if (await app.transfer.hasEntries()) setConfirming(true);
    else await redeem();
  };

  const tr = t.transfer;
  return (
    <section className={settingsStyles.section}>
      <h2 className={settingsStyles.heading}>{tr.title}</h2>
      <div className={styles.card}>
        <p className={styles.muted}>{tr.intro}</p>
        {issued ? (
          <>
            <p style={{ fontSize: "2rem", fontWeight: 700, letterSpacing: "0.12em", margin: 0 }} aria-label={tr.codeLabel}>{issued.code.slice(0, 3)} {issued.code.slice(3)}</p>
            <p className={styles.muted}>{tr.codeValid}</p>
          </>
        ) : (
          <div className={styles.actions}>
            <Button variant="primary" disabled={busy} onClick={() => void issue()}>{tr.getCode}</Button>
            {!entering && <Button variant="ghost" disabled={busy} onClick={() => setEntering(true)}>{tr.haveCode}</Button>}
          </div>
        )}
        {entering && !confirming && (
          <form className={styles.actions} onSubmit={(e) => { e.preventDefault(); void submit(); }}>
            <label className={styles.rowField} style={{ flex: 1, flexDirection: "column", alignItems: "stretch", gap: 4, fontSize: "0.8125rem", color: "var(--label2)" }}>
              {tr.codeLabel}
              <input inputMode="numeric" autoComplete="one-time-code" pattern="[0-9 ]{6,7}" maxLength={7} value={code} onChange={(e) => setCode(e.target.value)} required style={{ minHeight: "var(--tap)", padding: "0 12px", border: 0, borderRadius: 8, background: "var(--fill)", font: "inherit", color: "var(--label)", fontSize: "1.25rem", letterSpacing: "0.1em" }} />
            </label>
            <Button type="submit" variant="primary" disabled={busy || code.replace(/\D/g, "").length !== 6}>{tr.apply}</Button>
          </form>
        )}
        {confirming && (
          <div className={styles.actions} role="alertdialog" aria-label={tr.apply}>
            <p style={{ width: "100%", margin: 0 }}>{tr.replaceConfirm}</p>
            <Button variant="ghost" disabled={busy} onClick={() => setConfirming(false)}>{t.questionnaire.deleteNo}</Button>
            <Button variant="danger" disabled={busy} onClick={() => void redeem()}>{tr.replaceYes}</Button>
          </div>
        )}
        {notice && <p className={styles.muted} role="status">{notice}</p>}
        {error && <p className={styles.error} role="alert">{error}</p>}
      </div>
    </section>
  );
}
